import type { OutboxDocumentWrite } from '@colplat/core-client';
import { apiFetch } from './api.js';

/**
 * Send what was saved on this device while it had no connection.
 *
 * It lived in the Transactions page, which is where offline writes were first
 * built — so work saved offline from the wallet log, which is where entries
 * are made now, reached the server only once somebody happened to open
 * Transactions. Moved here, with its rules unchanged, so it runs from
 * whichever page is open (ADR-0011, amendment of 2026-10-21).
 *
 * In HLC order (`sendableDocuments` is ordered by it), and an entry leaves the
 * queue only on a definitive answer: a success, or a client error that says
 * retrying will never work. A transport failure leaves it queued for the next
 * reconnect — the outbox is the one client store whose loss is real data
 * loss, so it is never cleared on a maybe.
 */
export interface FlushResult {
  synced: number;
  /** Edits the server kept for review: the transaction moved on meanwhile. */
  held: number;
  /** Deletes refused because the record moved on — it comes back. */
  refusedDeletes: number;
  /** Entries the server will not accept as they are — kept, marked, shown. */
  refused: number;
}

export interface FlushDeps {
  token: string;
  sendable: () => Promise<OutboxDocumentWrite[]>;
  remove: (id: string) => Promise<void>;
  /** Keep the entry, with the server's reason, and stop sending it. */
  refuse: (id: string, message: string) => Promise<void>;
  /** Injectable for tests; `apiFetch` otherwise. */
  fetch?: typeof apiFetch;
}

export async function flushQueuedDocuments(deps: FlushDeps): Promise<FlushResult> {
  const send = deps.fetch ?? apiFetch;
  const out: FlushResult = { synced: 0, held: 0, refusedDeletes: 0, refused: 0 };
  // `sendable`, not everything: a held draft is unsent work the user has not
  // chosen to send, and flushing it would push it over whoever took the
  // record from them.
  for (const entry of await deps.sendable()) {
    if (entry.entity !== 'transaction') continue;
    // Provenance, and the reason the server accepts this without a lease: it
    // was written on this device with no connection and is only reaching the
    // server now (`source: 'offline'`, `withoutLease`).
    const body = { ...(entry.payload as Record<string, unknown>), queuedOffline: true };
    let res: Response;
    try {
      if (entry.op === 'restore') {
        // Putting a record back cannot destroy anything and is idempotent.
        res = await send(`/apps/webledger/deleted/transaction/${entry.recordId}/restore`,
          { method: 'POST', token: deps.token });
      } else if (entry.op === 'delete') {
        // The version it was deleted at, in the query string — `main.ts`
        // parses bodies for POST and PUT only. Refused if the record moved on.
        const base = (entry.payload as { baseVersion?: number } | null)?.baseVersion;
        res = await send(
          `/apps/webledger/transactions/${entry.recordId}?baseVersion=${encodeURIComponent(String(base ?? ''))}`,
          { method: 'DELETE', token: deps.token },
        );
      } else if (entry.op === 'create') {
        res = await send('/apps/webledger/transactions', { method: 'POST', token: deps.token, body });
      } else {
        res = await send(`/apps/webledger/transactions/${entry.recordId}`, { method: 'PUT', token: deps.token, body });
      }
    } catch {
      break; // still unreachable — the next reconnect tries again
    }

    if (res.ok) {
      await deps.remove(entry.id);
      out.synced += 1;
      continue;
    }
    const err = (await res.json().catch(() => ({}))) as { error?: string; message?: string; pendingRevisionId?: string };
    if (err.pendingRevisionId) {
      // The transaction moved on while we were away, so the server kept our
      // whole version for review rather than applying it over theirs. Out of
      // the outbox: it is no longer waiting to be sent, it is waiting for a
      // decision (the review banner).
      await deps.remove(entry.id);
      out.held += 1;
      continue;
    }
    if (entry.op === 'delete' && (res.status === 409 || res.status === 404)) {
      // 409: somebody changed it after the delete was decided — applying it
      // would erase their work. 404: already gone, which is what was asked.
      await deps.remove(entry.id);
      if (res.status === 404) out.synced += 1; else out.refusedDeletes += 1;
      continue;
    }
    if (entry.op === 'create' && res.status === 409) {
      // A create whose ack was lost: the id is client-minted, so "exists"
      // means the work is done.
      await deps.remove(entry.id);
      out.synced += 1;
    } else if (res.status >= 400 && res.status < 500) {
      // The server will never accept this as it is. Kept — silently dropping
      // someone's work is the worst outcome there is — with the server's own
      // words, and shown until it is changed or thrown away.
      await deps.refuse(entry.id, err.message ?? (err.error ? `refused: ${err.error}` : `refused (${res.status})`));
      out.refused += 1;
    } else {
      break; // 5xx: transient
    }
  }
  return out;
}

/** The sentence a flush earns, or nothing when it did nothing worth saying. */
export function flushSays(r: FlushResult): string {
  const parts: string[] = [];
  if (r.synced > 0) parts.push(`${r.synced} change${r.synced === 1 ? '' : 's'} saved offline ${r.synced === 1 ? 'was' : 'were'} sent`);
  if (r.held > 0) {
    parts.push(`${r.held} need${r.held === 1 ? 's' : ''} your review — the entry changed while you were offline`);
  }
  if (r.refusedDeletes > 0) {
    parts.push(`${r.refusedDeletes} delete${r.refusedDeletes === 1 ? ' was' : 's were'} not applied because somebody `
      + `changed ${r.refusedDeletes === 1 ? 'it' : 'them'} while you were offline — `
      + `${r.refusedDeletes === 1 ? 'it is' : 'they are'} back`);
  }
  if (r.refused > 0) {
    parts.push(`${r.refused} ${r.refused === 1 ? 'was' : 'were'} not accepted by the server — see the note at the top`);
  }
  return parts.length > 0 ? `${parts.join('; ')}.` : '';
}

/**
 * One run at a time: a call while one is in flight joins it, and the next
 * call after it has finished starts a new one.
 *
 * Cleared after the run is recorded, never inside it — a run that finishes
 * without ever awaiting (the browser knows it is offline) used to clear the
 * slot before it was filled, and then stood in for a run in progress for
 * ever: every later flush got its answer and nothing was sent again.
 */
export function singleFlight<T>(run: () => Promise<T>): () => Promise<T> {
  let current: Promise<T> | undefined;
  return () => {
    if (current) return current;
    // The `finally` runs only after the `await`, which is always after the
    // assignment below — and before anyone awaiting `mine` resumes, so the
    // next call after it has finished really does start a new run.
    const slot: { run?: Promise<T> } = {};
    slot.run = (async () => {
      try {
        return await run();
      } finally {
        if (current === slot.run) current = undefined;
      }
    })();
    current = slot.run;
    return slot.run;
  };
}

